import { cn } from '@/utils/cn';

export interface FieldClassOptions {
  /** Marks the control invalid (danger border and ring). */
  error?: boolean;
  /** `pill` for single-line controls (the RoofVox look), `box` for multi-line ones. */
  shape?: 'pill' | 'box';
  /** `md` (44px, default), `sm` (36px) for dense grids and filter rows, `xs` (28px) for workspace card forms.
   *  sm is still 44px on touch; xs grows to 40px. */
  size?: 'md' | 'sm' | 'xs';
}

/**
 * The one class recipe for text-like form controls (input, select, textarea). Use it directly on a raw
 * element when the Input/Select/Textarea components do not fit, instead of writing another local class
 * constant (there were ~22 of them).
 *
 *   - Pill, `ui-input` fill (Surface 3 in dark), 1px hairline that firms up on hover.
 *   - Focus: violet border plus a soft 4px ring (the brand's "focused state, violet ring").
 *   - 16px text on touch devices so iOS never zooms the page on focus (keyed on `pointer: coarse`, not on
 *     width, since iOS zooms by device); 14px (brand Body) with a mouse.
 *   - 44px tall (the brand's field height and the minimum touch target); `size: 'sm'` is 36px on mouse, 44px on touch.
 */
export function fieldClass({ error = false, shape = 'pill', size = 'md' }: FieldClassOptions = {}): string {
  return cn(
    'w-full border bg-ui-input px-4 text-sm text-ui-text placeholder:text-ui-placeholder touch-manipulation [@media(pointer:coarse)]:text-base',
    shape === 'pill'
      ? size === 'xs'
        ? 'h-7 rounded-full px-3 text-xs [@media(pointer:coarse)]:h-10'
        : size === 'sm'
          ? 'h-9 rounded-full [@media(pointer:coarse)]:h-11'
          : 'h-11 rounded-full'
      : size === 'xs'
        ? 'rounded-control px-3 py-1.5 text-xs'
        : 'rounded-card py-2.5',
    'transition-[border-color,box-shadow] duration-150',
    'focus:outline-none focus:ring-4',
    error
      ? 'border-danger focus:border-danger focus:ring-danger/20'
      : 'border-ui-border hover:border-ui-border3 focus:border-ui-ring focus:ring-ui-ring/25',
    'disabled:cursor-not-allowed disabled:bg-ui-subtle disabled:opacity-60',
  );
}
